import type { FunctionComponent, PropsWithChildren } from 'react';
import { lazy, Suspense } from 'react';

import { Skeleton } from '~/ui/core/skeleton/Skeleton';

import { PositionCloseHeader, PositionTileFrame } from '../position-management';
import {
    createUrlTileDefinition,
    type UrlCommentTileRendering,
    type UrlTileModule,
    type UrlTileRenderContext,
    withCommentsOpen,
} from '../url-tiles';
import {
    type AlignmentTilePayload,
    type AutomationTilePayload,
    alignmentTileCodec,
    alignmentTileSchema,
    automationTileCodec,
    automationTileSchema,
    type CompareTilePayload,
    compareTileCodec,
    compareTileSchema,
    isAlignmentTilePayload,
    type NewTaskTilePayload,
    newTaskTileCodec,
    newTaskTileSchema,
    POSITION_ALIGNMENT_AUTOMATION_TILE_SLOT_KEY,
    POSITION_ALIGNMENT_COMPARE_TILE_SLOT_KEY,
    POSITION_ALIGNMENT_TASK_TILE_SLOT_KEY,
    POSITION_ALIGNMENT_TILE_SLOT_KEY,
    type TaskTilePayload,
    taskTileCodec,
    taskTileSchema,
} from './alignment-tile-payload';

// The tile takes the row beside the team's 28rem member column, and at least 48rem, so a side tile scrolls the row
// instead of squeezing the position's attribute table.
const ALIGNMENT_TILE_WIDTH = 'max(min(48rem, 100%), calc(100% - 30rem))';

/** A lazily loaded tile, with a closable skeleton while its code loads. */
const TileSuspense: FunctionComponent<
    PropsWithChildren<{ readonly context: UrlTileRenderContext; readonly slotKey: string }>
> = ({ children, context, slotKey }) => (
    <Suspense
        fallback={
            <PositionTileFrame header={<PositionCloseHeader onClose={() => context.closeTile(slotKey)} />}>
                <div className="mt-xs">
                    <Skeleton height="12rem" width="100%" />
                </div>
            </PositionTileFrame>
        }
    >
        {children}
    </Suspense>
);

const lazyTile = <Props extends object>(load: () => Promise<FunctionComponent<Props>>) =>
    lazy(async () => ({ default: await load() }));

const COMPARE_TILE_WIDTH = 'min(40rem, 100vw)';

const CompareTile = lazyTile(async () => (await import('./CompareTile')).CompareTile);

const AlignmentTaskTile = lazyTile(async () => (await import('./AlignmentTaskTile')).AlignmentTaskTile);

const NewAlignmentTaskTile = lazyTile(async () => (await import('./AlignmentTaskTile')).NewAlignmentTaskTile);

const TASK_TILE_WIDTH = 'min(36rem, 100vw)';

const AlignmentAutomationTile = lazyTile(
    async () => (await import('./AlignmentAutomationTile')).AlignmentAutomationTile,
);

const AUTOMATION_TILE_WIDTH = 'min(500px, 100vw)';

const AlignmentCommentTile = lazyTile(async () => (await import('./AlignmentCommentTile')).AlignmentCommentTile);

const AlignmentTile = lazyTile(async () => (await import('./AlignmentTile')).AlignmentTile);

type AlignmentTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: AlignmentTilePayload;
};

export const alignmentUrlTileDefinition = createUrlTileDefinition({
    codec: alignmentTileCodec,
    commentsGroup: {
        groupKey: (payload) => `position-alignment.${payload.alignmentId}`,
        isCommentsOpen: (payload) => payload.isCommentsOpen === true,
        role: 'parent',
    },
    maxWidth: ALIGNMENT_TILE_WIDTH,
    minWidth: ALIGNMENT_TILE_WIDTH,
    render: ({ context, payload }: AlignmentTileProps) => (
        <TileSuspense context={context} slotKey={POSITION_ALIGNMENT_TILE_SLOT_KEY}>
            <AlignmentTile context={context} payload={payload} />
        </TileSuspense>
    ),
    schema: alignmentTileSchema,
    slotKey: () => POSITION_ALIGNMENT_TILE_SLOT_KEY,
    tileKey: (payload) => `${POSITION_ALIGNMENT_TILE_SLOT_KEY}.${payload.alignmentId}`,
    toProps: (payload, context): AlignmentTileProps => ({ context, payload }),
});

type CompareTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: CompareTilePayload;
};

export const compareUrlTileDefinition = createUrlTileDefinition({
    codec: compareTileCodec,
    maxWidth: COMPARE_TILE_WIDTH,
    minWidth: COMPARE_TILE_WIDTH,
    render: ({ context, payload }: CompareTileProps) => (
        <TileSuspense context={context} slotKey={POSITION_ALIGNMENT_COMPARE_TILE_SLOT_KEY}>
            <CompareTile context={context} payload={payload} />
        </TileSuspense>
    ),
    schema: compareTileSchema,
    slotKey: () => POSITION_ALIGNMENT_COMPARE_TILE_SLOT_KEY,
    tileKey: (payload) => `${POSITION_ALIGNMENT_COMPARE_TILE_SLOT_KEY}.${payload.alignmentId}`,
    toProps: (payload, context): CompareTileProps => ({ context, payload }),
});

type TaskTileProps = { readonly context: UrlTileRenderContext; readonly payload: TaskTilePayload };

export const taskUrlTileDefinition = createUrlTileDefinition({
    codec: taskTileCodec,
    maxWidth: TASK_TILE_WIDTH,
    minWidth: TASK_TILE_WIDTH,
    render: ({ context, payload }: TaskTileProps) => (
        <TileSuspense context={context} slotKey={POSITION_ALIGNMENT_TASK_TILE_SLOT_KEY}>
            <AlignmentTaskTile context={context} payload={payload} />
        </TileSuspense>
    ),
    schema: taskTileSchema,
    slotKey: () => POSITION_ALIGNMENT_TASK_TILE_SLOT_KEY,
    tileKey: (payload) => `${POSITION_ALIGNMENT_TASK_TILE_SLOT_KEY}.${payload.taskId}`,
    toProps: (payload, context): TaskTileProps => ({ context, payload }),
});

type NewTaskTileProps = { readonly context: UrlTileRenderContext; readonly payload: NewTaskTilePayload };

export const newTaskUrlTileDefinition = createUrlTileDefinition({
    codec: newTaskTileCodec,
    maxWidth: TASK_TILE_WIDTH,
    minWidth: TASK_TILE_WIDTH,
    render: ({ context, payload }: NewTaskTileProps) => (
        <TileSuspense context={context} slotKey={POSITION_ALIGNMENT_TASK_TILE_SLOT_KEY}>
            <NewAlignmentTaskTile context={context} payload={payload} />
        </TileSuspense>
    ),
    schema: newTaskTileSchema,
    slotKey: () => POSITION_ALIGNMENT_TASK_TILE_SLOT_KEY,
    tileKey: (payload) => `${POSITION_ALIGNMENT_TASK_TILE_SLOT_KEY}.new.${payload.alignmentId}`,
    toProps: (payload, context): NewTaskTileProps => ({ context, payload }),
});

type AutomationTileProps = { readonly context: UrlTileRenderContext; readonly payload: AutomationTilePayload };

export const automationUrlTileDefinition = createUrlTileDefinition({
    codec: automationTileCodec,
    maxWidth: AUTOMATION_TILE_WIDTH,
    minWidth: AUTOMATION_TILE_WIDTH,
    render: ({ context, payload }: AutomationTileProps) => (
        <TileSuspense context={context} slotKey={POSITION_ALIGNMENT_AUTOMATION_TILE_SLOT_KEY}>
            <AlignmentAutomationTile context={context} payload={payload} />
        </TileSuspense>
    ),
    schema: automationTileSchema,
    slotKey: () => POSITION_ALIGNMENT_AUTOMATION_TILE_SLOT_KEY,
    tileKey: (payload) =>
        `${POSITION_ALIGNMENT_AUTOMATION_TILE_SLOT_KEY}.${payload.alignmentId}.${payload.taskId}.${payload.automationId}`,
    toProps: (payload, context): AutomationTileProps => ({ context, payload }),
});

/** Renders the comment tile of an alignment tile; `null` for comment tiles other features own. */
export const renderAlignmentCommentTile: UrlCommentTileRendering['renderCommentTile'] = ({ commentTile, context }) => {
    const owner = commentTile.ownerPayload;
    if (!isAlignmentTilePayload(owner)) return null;

    return (
        <Suspense fallback={null}>
            <AlignmentCommentTile payload={owner} onClose={() => context.updateTile(withCommentsOpen(owner, false))} />
        </Suspense>
    );
};

export const positionAlignmentUrlTileModule: UrlTileModule = {
    definitions: [
        alignmentUrlTileDefinition,
        compareUrlTileDefinition,
        taskUrlTileDefinition,
        newTaskUrlTileDefinition,
        automationUrlTileDefinition,
    ],
};
